/**
 * VEO-3 Video Generation Service
 * Uses server-side proxy to generate videos with VEO-3
 * API key is stored securely on server, not exposed to browser
 */

import { LIMITS } from '../constants/app';
import logger from '../utils/logger';
import type { ChatMessage, ConversationContext } from '../types/api';
import { fileStorageService } from './FileStorageService';
import { supabase } from '../lib/supabase';
import { geminiOmniRetiredMessage } from '../../server/config/geminiOmniRetirement';

interface VeoVideoResponse {
  content: string;
  type: 'video';
  context: ConversationContext;
  videoUrl?: string;
  operationId?: string;
}

class VeoVideoService {
  private apiBase: string;

  constructor() {
    // Use relative URL in production, localhost for development
    this.apiBase = process.env.REACT_APP_API_BASE || 
      (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
        ? 'http://localhost:3001' 
        : '');
  }

  /**
   * Convert image URL to base64 for sending to server
   */
  private async toInlineData(imageUrl: string): Promise<{ data: string; mimeType: string } | null> {
    try {
      if (imageUrl.startsWith('__BLOB_URL__:')) {
        imageUrl = imageUrl.replace('__BLOB_URL__:', '');
      }
      if (imageUrl.startsWith('data:')) {
        const m = imageUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (m) return { mimeType: m[1], data: m[2] };
        return null;
      }
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const mimeType = blob.type || 'image/png';
      
      // Use FileReader to convert blob to base64 (avoids stack overflow for large images)
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          // Extract base64 part from data URL
          const base64Data = dataUrl.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      return { data: base64, mimeType };
    } catch (e) {
      logger.warn('VEO: failed to inline image', e);
      return null;
    }
  }

  /**
   * Get current user ID for credit tracking
   */
  private async getUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch {
      return null;
    }
  }

  async generateVideo(
    prompt: string,
    context: ConversationContext | null = null,
    aspectRatio: string = '16:9',
    modelOverride?: string,
    durationSeconds?: string,
    nodeId?: string,
    flowId?: string,
    resolution?: string,
    mode: 'text-to-video' | 'image-to-video' | 'first-last-frame' = 'text-to-video',
    firstImage?: string,
    lastImage?: string,
    // Undefined = don't send a flag (server preserves its always-on default).
    // Only the VEO node passes this; Gemini Omni has no audio UI and is excluded
    // below since it uses a separate route.
    enableAudio?: boolean
  ): Promise<VeoVideoResponse> {
    // Retired Gemini Omni preview (Google shutdown 2026-09-30): refuse before any
    // request, so nothing is reserved. Outside the try so the message reaches the
    // node verbatim instead of as "Failed to generate video: ...".
    const retired = geminiOmniRetiredMessage(modelOverride);
    if (retired) throw new Error(retired);

    try {
      // Build the video prompt with context if provided
      let fullPrompt = prompt;

      if (context && context.messages) {
        const contextTexts: string[] = [];

        for (const msg of context.messages) {
          if (typeof msg.content === 'string') {
            if (msg.role !== 'system') {
              contextTexts.push(msg.content);
            }
          } else if (Array.isArray(msg.content)) {
            for (const part of msg.content) {
              if (part.type === 'text') {
                contextTexts.push(part.text);
              }
            }
          }
        }

        if (contextTexts.length > 0) {
          const contextText = contextTexts.join('\n');
          fullPrompt = `Context: ${contextText}\n\nVideo prompt: ${prompt}`;
        }
      }

      // Extract images from context or use explicitly provided images
      let imageData: { imageBytes: string; mimeType: string } | undefined;
      let lastImageData: { imageBytes: string; mimeType: string } | undefined;
      
      // If explicit first/last images provided (from mode-aware handler), use those
      if (firstImage) {
        const inline = await this.toInlineData(firstImage);
        if (inline) {
          imageData = { imageBytes: inline.data, mimeType: inline.mimeType };
          logger.debug('First frame image provided explicitly:', inline.mimeType);
        }
      }
      if (lastImage) {
        const inline = await this.toInlineData(lastImage);
        if (inline) {
          lastImageData = { imageBytes: inline.data, mimeType: inline.mimeType };
          logger.debug('Last frame image provided explicitly:', inline.mimeType);
        }
      }
      
      // Fall back to extracting the most recent image from context (for image-to-video without explicit images)
      if (!imageData && mode !== 'text-to-video' && context?.messages) {
        for (let i = context.messages.length - 1; i >= 0; i--) {
          const msg = context.messages[i];
          if (Array.isArray(msg.content)) {
            for (const part of msg.content) {
              if (part.type === 'image' && part.imageUrl) {
                const inline = await this.toInlineData(part.imageUrl);
                if (inline) {
                  imageData = { imageBytes: inline.data, mimeType: inline.mimeType };
                  logger.debug('Image found in context for video generation:', inline.mimeType);
                  break;
                }
              }
            }
          }
          if (imageData) break;
        }
      }

      const model = modelOverride || 'veo-3.1-fast-generate-preview';
      const duration = typeof durationSeconds === 'string' ? parseFloat(durationSeconds) : (durationSeconds || 8);

      // Get user ID for credit tracking
      const userId = await this.getUserId();

      console.log('🎬 [VEO-3 Client] Starting video generation:', {
        model,
        prompt: fullPrompt.substring(0, 100) + '...',
        aspectRatio,
        duration,
        mode,
        hasImage: !!imageData,
        hasLastImage: !!lastImageData,
        hasUserId: !!userId
      });

      // Build headers with optional user ID for credit tracking
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (userId) {
        headers['x-user-id'] = userId;
      }

      // Gemini Omni uses Google's SYNCHRONOUS Interactions API (not VEO's async
      // operation + polling flow), so it has a dedicated route that returns the
      // finished video URL directly.
      if (model.startsWith('gemini-omni')) {
        const omniResp = await fetch(`${this.apiBase}/api/gemini-omni/create`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: fullPrompt,
            model,
            aspectRatio,
            durationSeconds: duration,
            resolution: resolution || '720p',
            nodeId,
            flowId,
            mode,
            image: imageData,
          }),
        });
        if (!omniResp.ok) {
          const errorData = await omniResp.json().catch(() => ({}));
          // 410 "Model retired": the explanation is in `message`, not `error`.
          if (omniResp.status === 410 && errorData.message) throw new Error(errorData.message);
          throw new Error(errorData.error || errorData.message || `Gemini Omni generation failed: ${omniResp.status}`);
        }
        const omniData = await omniResp.json();
        return {
          content: omniData.videoUrl,
          type: 'video',
          context: context ?? ({ messages: [] } as ConversationContext),
          videoUrl: omniData.videoUrl,
        };
      }

      // Step 1: Create video generation job
      const createResponse = await fetch(`${this.apiBase}/api/veo/video/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: fullPrompt,
          model,
          aspectRatio,
          durationSeconds: duration,
          resolution: resolution || '720p', // Pass resolution for credit pricing
          nodeId,
          flowId,
          mode,
          image: imageData,
          lastImage: lastImageData,
          // Only include when the caller specified it → server maps to
          // GenerateVideosConfig.generateAudio (verified field). Omitting it
          // preserves VEO's always-on default.
          ...(typeof enableAudio === 'boolean' ? { generateAudio: enableAudio } : {}),
        })
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to start video generation: ${createResponse.status}`);
      }

      const { operationId } = await createResponse.json();
      logger.debug('VEO-3 operation started:', operationId);

      // Step 2: Poll for completion
      let pollCount = 0;
      const maxPolls = 40;
      let pollInterval = 2000;
      const maxPollInterval = 15000;
      const backoffMultiplier = 1.3;

      while (pollCount < maxPolls) {
        const currentInterval = Math.min(pollInterval, maxPollInterval);
        logger.debug(`Polling VEO-3... (${pollCount + 1}/${maxPolls}, waiting ${currentInterval}ms)`);

        await new Promise(resolve => setTimeout(resolve, currentInterval));

        const statusResponse = await fetch(`${this.apiBase}/api/veo/video/${encodeURIComponent(operationId)}`);
        
        if (!statusResponse.ok) {
          const errorData = await statusResponse.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to check video status');
        }

        const status = await statusResponse.json();

        if (status.done) {
          if (status.status === 'failed') {
            throw new Error(status.error || 'Video generation failed');
          }

          logger.debug('VEO-3 video completed:', status.videoUri?.substring(0, 50));

          // The server already persisted the video to R2 (with the owner's key)
          // and returns its media-proxy URL. Use it: downloading and uploading
          // it again here stored every tab-open generation twice.
          if (typeof status.videoUrl === 'string' && status.videoUrl) {
            const persistedUrl: string = status.videoUrl;
            const responseText = 'Video generated successfully and ready to play!';
            const updatedMessages: ChatMessage[] = [
              ...(context?.messages || []).slice(-LIMITS.MAX_CONTEXT_MESSAGES),
              { role: 'user' as const, content: `Video generation request: ${prompt}` },
              { role: 'assistant' as const, content: responseText }
            ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);
            return {
              content: persistedUrl,
              type: 'video',
              videoUrl: persistedUrl,
              operationId,
              context: { messages: updatedMessages }
            };
          }

          // Fallback for servers/rows without a persisted videoUrl.
          // Step 3: Download the video
          const downloadResponse = await fetch(`${this.apiBase}/api/veo/video/${encodeURIComponent(operationId)}/download`);
          
          if (!downloadResponse.ok) {
            throw new Error('Failed to download generated video');
          }

          const videoBlob = await downloadResponse.blob();
          logger.debug('Video downloaded:', {
            sizeKB: Math.round(videoBlob.size / 1024),
            type: videoBlob.type
          });

          // Save video to storage
          let videoUrl: string;
          try {
            const videoFile = new File([videoBlob], `veo-video-${Date.now()}.mp4`, { 
              type: videoBlob.type || 'video/mp4' 
            });
            const fileInfo = await fileStorageService.uploadFile(videoFile, {
              folder: 'generated-videos',
              isPublic: true,
              scope: flowId ? 'flow' : 'global',
              flowId: flowId || undefined
            });
            videoUrl = fileInfo.url;
            logger.debug('Video saved to storage:', fileInfo.id, 'flowId:', flowId || 'global');
          } catch (storageError) {
            logger.error('Failed to save video to storage, using blob URL:', storageError);
            videoUrl = URL.createObjectURL(videoBlob);
          }

          // Create response
          const responseText = 'Video generated successfully and ready to play!';
          const updatedMessages: ChatMessage[] = [
            ...(context?.messages || []).slice(-LIMITS.MAX_CONTEXT_MESSAGES),
            { role: 'user' as const, content: `Video generation request: ${prompt}` },
            { role: 'assistant' as const, content: responseText }
          ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);

          return {
            content: videoUrl,
            type: 'video',
            videoUrl,
            operationId,
            context: { messages: updatedMessages }
          };
        }

        pollCount++;
        pollInterval = Math.floor(pollInterval * backoffMultiplier);
      }

      throw new Error('Video generation timed out. Please try again later.');

    } catch (error) {
      const errorObj = error as { message?: string };
      logger.error('VEO-3 Video Generation Error:', errorObj.message);

      let errorMessage = `Failed to generate video: ${errorObj.message || 'Unknown error'}`;

      if (errorObj.message?.includes('billing')) {
        errorMessage += '\n\n💡 Solution: VEO-3 requires billing to be enabled in Google AI Studio.';
      } else if (errorObj.message?.includes('access') || errorObj.message?.includes('PERMISSION')) {
        errorMessage += '\n\n💡 Solution: Your API key may not have access to VEO-3.';
      } else if (errorObj.message?.includes('rate') || errorObj.message?.includes('EXHAUSTED')) {
        errorMessage += '\n\n💡 Solution: Rate limit exceeded. Please wait before trying again.';
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Check if VEO-3 is configured (server has API key)
   * We assume it's configured since we can't check server env from client
   */
  isConfigured(): boolean {
    return true; // Server handles the API key now
  }
}

const veoVideoServiceInstance = new VeoVideoService();
export default veoVideoServiceInstance;
